import React, { useState, useRef, useEffect } from 'react';
import { PAGE_TYPES, type PageType } from '../types/editing';

interface InlinePageTypeEditorProps {
  pageType: PageType;
  pageIndex: number;
  pageLabel: 'Left' | 'Right';
  onChange: (newPageType: PageType) => void;
  getPageTypeColor: (pageType?: string) => string;
  getPageTypeDisplay: (pageType?: string) => string;
}

const InlinePageTypeEditor: React.FC<InlinePageTypeEditorProps> = ({
  pageType,
  pageIndex,
  pageLabel,
  onChange,
  getPageTypeColor,
  getPageTypeDisplay
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState<PageType>(pageType);
  const selectRef = useRef<HTMLSelectElement>(null);

  // Update tempValue when pageType prop changes
  useEffect(() => {
    setTempValue(pageType);
  }, [pageType]);

  // Focus select when entering edit mode
  useEffect(() => {
    if (isEditing && selectRef.current) {
      selectRef.current.focus();
    }
  }, [isEditing]);

  const handleClick = () => {
    setIsEditing(true);
    setTempValue(pageType);
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value as PageType;
    setTempValue(newValue);
    // Call onChange immediately when value changes
    onChange(newValue);
  };

  const handleBlur = () => {
    // onChange was already called in handleChange, so just close the editor
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLSelectElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // onChange was already called in handleChange, so just close the editor
      setIsEditing(false);
    } else if (e.key === 'Escape') {
      // Revert to original value if it changed
      if (tempValue !== pageType) {
        onChange(pageType);
      }
      setTempValue(pageType);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    const bgColor = getPageTypeColor(tempValue || '');
    return (
      <select
        ref={selectRef}
        value={tempValue || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        style={{
          fontSize: '9px',
          fontWeight: 500,
          padding: '3px 8px',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          backgroundColor: bgColor || '#cccccc',
          color: tempValue && tempValue.trim() !== '' ? 'white' : '#666',
          cursor: 'pointer',
          minWidth: '80px',
          width: 'auto',
          outline: 'none !important',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          textTransform: 'uppercase',
          letterSpacing: '0.3px',
        }}
        onFocus={(e) => {
          e.target.style.outline = 'none';
          e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.2)';
        }}
      >
        <option value="" style={{ backgroundColor: '#cccccc', color: '#666' }}>-- No Page Type --</option>
        {PAGE_TYPES.map(type => (
          <option 
            key={type} 
            value={type}
            style={{ 
              backgroundColor: getPageTypeColor(type),
              color: 'white'
            }}
          >
            {getPageTypeDisplay(type)}
          </option>
        ))}
      </select>
    );
  }

  const displayText = pageType && pageType.trim() !== '' 
    ? getPageTypeDisplay(pageType)
    : 'No Page Type';
  const backgroundColor = pageType && pageType.trim() !== ''
    ? getPageTypeColor(pageType)
    : '#cccccc';

  return (
    <div
      onClick={handleClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 8px',
        borderRadius: '12px',
        backgroundColor,
        color: pageType && pageType.trim() !== '' ? 'white' : '#666',
        fontSize: '9px',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
        minWidth: '50px',
        whiteSpace: 'nowrap',
      }}
      title={`Click to edit ${pageLabel} page type`}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = '0.8';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '1';
      }}
    >
      {displayText}
    </div>
  );
};

export default InlinePageTypeEditor;

